import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Article as ArticleIcon,
  PlayCircleOutline as PlayCircleIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import apiService from '../../services/apiService';
import { LoadingState } from '../LoadingState';

interface TrainingContent {
  id: number;
  title: string;
  description: string;
  content_type: string;
  category_id: string;
  content?: string;
  content_url?: string;
  active: boolean;
  view_count?: number;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface ContentViewModalProps {
  content: TrainingContent | null;
  open: boolean;
  onClose: () => void;
}

const ContentViewModal: React.FC<ContentViewModalProps> = ({ content, open, onClose }) => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();

  if (!content) return null;

  const renderContent = () => {
    switch (content.content_type) {
      case 'VIDEO':
        return (
          <Box sx={{ width: '100%', height: '500px' }}>
            <iframe
              src={content.content_url}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
            />
          </Box>
        );
      case 'PDF':
        return (
          <Box sx={{ width: '100%', height: '500px' }}>
            <iframe
              src={content.content_url}
              width="100%"
              height="100%"
              frameBorder="0"
            />
          </Box>
        );
      case 'ARTICLE':
        return (
          <Box sx={{ width: '100%', maxHeight: '500px', overflowY: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: content.content || '' }} />
          </Box>
        );
      default:
        if (content.content) {
          return (
            <Box sx={{ width: '100%', maxHeight: '500px', overflowY: 'auto' }}>
              <div dangerouslySetInnerHTML={{ __html: content.content }} />
            </Box>
          );
        }
        if (content.content_url) {
          return (
            <Box sx={{ width: '100%', height: '500px' }}>
              <iframe
                src={content.content_url}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
              />
            </Box>
          );
        }
        return <Typography>No preview available</Typography>;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ pr: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {content.title}
        {isAdmin() && (
          <IconButton
            size="small"
            onClick={() => {
              onClose();
              navigate(`/app/training/${content.id}?edit=true`);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <EditIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {content.description && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            {content.description}
          </Typography>
        )}
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const TrainingTab: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const [content, setContent] = useState<TrainingContent[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedContent, setSelectedContent] = useState<number[]>([]);
  const [viewContent, setViewContent] = useState<TrainingContent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchContent();
    fetchCategories();
  }, []);

  // Initialize expanded categories after fetching content
  useEffect(() => {
    if (categories.length > 0) {
      const categoryIds = new Set(categories.map(cat => cat.id));
      setExpandedCategories(categoryIds);
    }
  }, [categories]);

  const fetchContent = async () => {
    try {
      const response = await apiService.get<TrainingContent[]>('/api/training/content');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching training content:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.get<Category[]>('/api/training/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Extract preview content based on content type
  const getContentPreview = (item: TrainingContent) => {
    const previewContentStyles = {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    const contentType = item.content_type.toUpperCase();

    switch (contentType) {
      case 'EMBED':
      case 'VIDEO':
        return (
          <Box sx={previewContentStyles}>
            <PlayCircleIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Box>
        );
      case 'PDF':
        return (
          <Box sx={previewContentStyles}>
            <PdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Box>
        );
      case 'ARTICLE':
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = item.content || '';
        const textContent = tempDiv.textContent || '';
        return (
          <Box 
            sx={{
              ...previewContentStyles,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              p: 2,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '60px',
                background: 'linear-gradient(180deg, transparent, #fafafa)'
              }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 6,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {textContent.slice(0, 300) + (textContent.length > 300 ? '...' : '')}
            </Typography>
          </Box>
        );
      default:
        return (
          <Box sx={previewContentStyles}>
            <ArticleIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          </Box>
        );
    }
  };

  // Group content by categories
  const contentByCategory = categories.reduce((acc, category) => {
    const categoryContent = content.filter(item => 
      item.category_id === category.id && (item.active || isAdmin())
    );
    if (categoryContent.length > 0) {
      acc[category.id] = {
        name: category.name,
        content: categoryContent
      };
    }
    return acc;
  }, {} as Record<string, { name: string; content: TrainingContent[] }>);

  const handleSelect = (contentId: number) => {
    setSelectedContent(prev =>
      prev.includes(contentId)
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };

  const handleCardClick = async (item: TrainingContent) => {
    try {
      // Track view only if user is authenticated
      if (auth.isAuthenticated) {
        await apiService.get(`/api/training/content/${item.id}`);
      }
      
      const contentType = item.content_type.toUpperCase();
      if ((contentType === 'VIDEO' || contentType === 'EMBED') && item.content_url) {
        window.open(item.content_url, '_blank');
      } else if (contentType === 'PDF' && item.content_url) {
        window.open(item.content_url, '_blank');
      } else {
        setViewContent(item);
        setIsModalOpen(true);
      }
    } catch (error) {
      // Only log tracking errors if user is authenticated
      if (auth.isAuthenticated) {
        console.error('Error tracking content view:', error);
      } else {
        console.error('Error opening content:', error);
      }
    }
  };

  const handleDelete = async () => {
    try {
      await Promise.all(
        selectedContent.map(id =>
          apiService.delete(`/api/training/content/${id}`)
        )
      );
      setSelectedContent([]);
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
      setDeleteError('Failed to delete selected content. Please try again.');
    }
  };

  const handleAccordionChange = (categoryId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategories(prev => {
      const newExpanded = new Set(prev);
      if (isExpanded) {
        newExpanded.add(categoryId);
      } else {
        newExpanded.delete(categoryId);
      }
      return newExpanded;
    });
  };

  if (isLoading) {
    return <LoadingState message="Loading training content..." containerClassName="min-h-[400px]" />;
  }

  return (
    <Box sx={{ px: 3, pb: 3, pt: 0 }}>
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
        {isAdmin() && selectedContent.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete Selected ({selectedContent.length})
          </Button>
        )}
        {isAdmin() && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/training/new')}
          >
            Create Training Content
          </Button>
        )}
      </Box>

      {Object.entries(contentByCategory).map(([categoryId, { name, content }]) => (
        <Accordion
          key={categoryId}
          expanded={expandedCategories.has(categoryId)}
          onChange={handleAccordionChange(categoryId)}
          sx={{
            mb: 0.5,
            '&:before': {
              display: 'none',
            },
            '& .MuiAccordionSummary-root': {
              minHeight: '48px',
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'grey.200',
              '&:hover': {
                bgcolor: 'grey.200',
              },
            },
            '& .MuiAccordionSummary-content': {
              m: 0,
              py: 1,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'text.primary',
              }}
            >
              {name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2, px: 2, pb: 1 }}>
            <Grid container spacing={2}>
              {content.map((item) => (
                <Grid item xs={12} sm={6} lg={4} key={item.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      position: 'relative',
                      height: '100%',
                      maxHeight: 340,
                      boxShadow: 2,
                      transition: 'all 0.2s ease-in-out',
                      border: '1px solid',
                      borderColor: 'grey.100',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        borderColor: 'grey.200'
                      },
                      ...((!item.active && !isAdmin()) && {
                        display: 'none'
                      }),
                      ...((!item.active && isAdmin()) && {
                        bgcolor: 'grey.100',
                        '& .MuiTypography-root': {
                          color: 'text.secondary'
                        }
                      })
                    }}
                    onClick={() => handleCardClick(item)}
                  >
                    {isAdmin() && (
                      <Checkbox
                        className="select-checkbox"
                        checked={selectedContent.includes(item.id)}
                        onChange={() => handleSelect(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          zIndex: 1,
                          boxShadow: 1
                        }}
                      />
                    )}
                    {isAdmin() && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/training/${item.id}?edit=true`);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          boxShadow: 1,
                          zIndex: 1,
                          '&:hover': {
                            bgcolor: 'background.paper',
                            boxShadow: 2
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Box sx={{ 
                      position: 'relative',
                      width: '100%',
                      paddingTop: '56.25%', // 16:9 aspect ratio (9/16 = 0.5625)
                      bgcolor: 'grey.50',
                      borderBottom: 1,
                      borderColor: 'grey.100',
                      overflow: 'hidden'
                    }}>
                      {getContentPreview(item)}
                    </Box>
                    <CardContent sx={{ 
                      pt: 2,
                      px: 2,
                      pb: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5
                    }}>
                      <Typography 
                        variant="h6" 
                        component="h2"
                        sx={{ 
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          lineHeight: 1.2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          margin: 0
                        }}
                      >
                        {item.title}
                        {!item.active && isAdmin() && (
                          <Typography 
                            component="span" 
                            sx={{ 
                              ml: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'normal',
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            (Disabled)
                          </Typography>
                        )}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.3,
                          margin: 0
                        }}
                      >
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <ContentViewModal
        content={viewContent}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setViewContent(null);
        }}
      />
    </Box>
  );
};

export default TrainingTab;